import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { read, utils } from "xlsx";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";

import { apiClient, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle } from "@/features/ui";
import { useSnackbar } from "@/features/ui";

export const BulkUploadPromotionCodesDialog = ({
  isOpen,
  onClose,
  onSubmitted,
  promotionId,
}) => {
  const { showSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setParsedRows([]);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);

    const data = await file.arrayBuffer();
    const workbook = read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = utils.sheet_to_json(sheet);

    const mapped = rows.map((row) => ({
      customerId: String(row.customerId || row.customer_id || ""),
    }));

    setParsedRows(mapped);
  };

  const handleSubmit = async () => {
    if (parsedRows.length === 0) return;

    setIsSubmitting(true);
    try {
      await apiClient.post(
        `promotions/${promotionId}/promotion-codes/bulk`,
        { promotionCodes: parsedRows }
      );

      onSubmitted();
      handleClose();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error)
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      <CustomDialogTitle
        variant="h5"
        title="Upload Bulk Kode Promosi"
        onClose={handleClose}
      />
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Upload file Excel (.xlsx, .xls) dengan kolom:{" "}
            <b>customerId</b>
          </Typography>
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </Box>
          {fileName && (
            <Typography variant="body2">
              File: {fileName} — {parsedRows.length} baris ditemukan
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          loading={isSubmitting}
          variant="contained"
          onClick={handleSubmit}
          disabled={parsedRows.length === 0}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkUploadPromotionCodesDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
  promotionId: PropTypes.string.isRequired,
};
